<template>
  <div class="bank-statement-page">
    <div class="bank-statement-content">
      <div v-if="loading" class="loading-state">
        <BaseSpinner />
        <p>Loading bank statements...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <i class="las la-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="loadDocuments" class="retry-btn">Retry</button>
      </div>

      <div v-else class="bank-statement-sections">
        <div class="bank-statement-section-header">
          <h2 class="bank-statement-section-title">Bank Statements</h2>
          <div class="status-legend">
            <span class="status-legend-item">
              <span class="status-badge status-completed"><i class="las la-check"></i></span>
              <span>Completed</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-pending"><i class="las la-clock"></i></span>
              <span>Pending</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-processing"><i class="las la-sync"></i></span>
              <span>Processing</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-error"><i class="las la-times"></i></span>
              <span>Error</span>
            </span>
          </div>
        </div>

        <div class="bank-statement-section">
          <div class="section-header">
            <div class="section-header-actions">
              <label v-if="documents.length > 0" class="select-all-checkbox">
                <input
                  type="checkbox"
                  :checked="allDocumentsSelected"
                  @change="toggleSelectAll"
                />
                <span>Select all</span>
              </label>
              <div class="section-header-buttons">
                <BaseButton
                  v-if="documents.length > 0"
                  type="button"
                  stylevariant="primary"
                  :disabled="generatingCombinedReport"
                  :loading="generatingCombinedReport"
                  :cus_style="{ width: 'auto' }"
                  class="upload-section-btn"
                  @click="generateCombinedReport"
                >
                  <i v-if="!generatingCombinedReport" class="las la-chart-bar"></i>
                  {{ generatingCombinedReport ? 'Generating...' : 'Generate Combined Report' }}
                </BaseButton>
                <BaseButton
                  stylevariant="primary"
                  :cus_style="{ width: 'auto' }"
                  @click="uploadDocument"
                  class="upload-section-btn"
                >
                  <i class="las la-plus"></i>
                  Upload
                </BaseButton>
              </div>
            </div>
          </div>

          <!-- Bulk Actions Bar -->
          <div v-if="selectedDocuments.length > 0" class="bulk-actions-bar">
            <div class="bulk-actions-info">
              <span>{{ selectedDocuments.length }} file{{ selectedDocuments.length !== 1 ? 's' : '' }} selected</span>
            </div>
            <div class="bulk-actions-buttons">
              <button
                @click="deleteSelectedDocuments"
                class="bulk-delete-btn"
                :disabled="deletingFiles"
                title="Delete Selected"
              >
                <BaseSpinner v-if="deletingFiles" size="small" />
                <i v-else class="las la-trash"></i>
              </button>
              <button @click="clearSelection" class="bulk-cancel-btn" title="Cancel">
                <i class="las la-times"></i>
              </button>
            </div>
          </div>

          <div v-if="documents.length === 0" class="empty-document">
            <i class="las la-university"></i>
            <p>No bank statements uploaded</p>
          </div>
          <div v-else class="documents-list">
            <div
              v-for="doc in documents"
              :key="doc.id"
              class="document-card"
              :class="{ selected: isDocumentSelected(doc.id) }"
            >
              <div class="document-card-content">
                <label class="file-checkbox">
                  <input
                    type="checkbox"
                    :checked="isDocumentSelected(doc.id)"
                    @change="toggleDocumentSelection(doc)"
                  />
                </label>
                <span
                  class="document-file-icon"
                  :class="{ 'pdf-file': isPdfFile(doc), 'excel-file': isExcelFile(doc) }"
                  @click="viewDocument(doc)"
                >
                  <i :class="getFileIcon(doc)"></i>
                </span>
                <span class="document-name">{{ formatName(doc.name || doc.file_name || 'Bank Statement') }}</span>
                <span class="document-date">{{ formatDate(doc.uploaded_at) }}</span>
                <span class="status-badge" :class="getStatusClass(doc.status)">
                  <i :class="getStatusIcon(doc.status)"></i>
                </span>
                <button
                  v-if="doc.status === 'completed' || doc.status === 'success'"
                  class="view-btn"
                  @click="viewDocument(doc)"
                  title="View Document"
                >
                  <i class="las la-external-link-alt"></i>
                </button>
                <BaseSpinner
                  v-if="generatingReport === (doc.id || doc.file_id)"
                  size="small"
                  class="report-btn-spinner"
                />
                <button
                  v-else
                  class="report-btn"
                  :disabled="isReportDisabled(doc)"
                  @click="generateReport(doc)"
                  title="Generate Report"
                >
                  <i class="las la-chart-bar"></i>
                </button>
                <button class="delete-btn" @click="deleteDocument(doc)" title="Delete Document">
                  <i class="las la-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import BaseSpinner from "@/components/UI/BaseSpinner.vue";
import BaseButton from "@/components/UI/BaseButton.vue";

export default {
  name: 'BankStatementPage',
  components: { BaseSpinner, BaseButton },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      documents: [],
      selectedDocuments: [],
      deletingFiles: false,
      generatingReport: null,
      generatingCombinedReport: false,
      candidateName: ''
    };
  },
  computed: {
    allDocumentsSelected() {
      return this.documents.length > 0 && this.selectedDocuments.length === this.documents.length;
    }
  },
  async mounted() {
    await Promise.all([
      this.loadDocuments(),
      this.loadCandidate()
    ]);
  },
  methods: {
    async loadCandidate() {
      try {
        const candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
        this.candidateName = candidate?.name || candidate?.company || '';
      } catch (err) {
        console.error('Error loading candidate:', err);
        this.candidateName = '';
      }
    },

    async loadDocuments() {
      this.loading = true;
      this.error = null;
      try {
        const response = await ChatbotService.getUploads(this.candidateId, {
          type: 'bank_statement,multi_account_statement',
          page: 1,
          page_size: 100
        });
        const docs = Array.isArray(response) ? response : (response.data || response.files || []);
        this.documents = docs.map(d => ({
          ...d,
          status: d.scan_status || d.status || 'pending'
        }));
      } catch (err) {
        console.error('Error loading bank statements:', err);
        this.error = 'Failed to load bank statements';
      } finally {
        this.loading = false;
      }
    },
    uploadDocument() {
      this.$router.push({
        name: 'chatbot-upload',
        params: { candidateId: this.candidateId },
        query: { type: 'bank_statement', file_type: 'bank_statement' }
      });
    },
    viewDocument(doc) {
      const fileId = doc.id || doc.file_id;
      if (fileId) {
        this.$router.push({
          name: 'chatbot-file-details',
          params: { candidateId: this.candidateId, fileId }
        });
      }
    },
    isReportDisabled(doc) {
      return this.generatingReport === (doc.id || doc.file_id)
        || ['scanning', 'extracting', 'pending'].includes(doc.status);
    },
    async generateReport(doc) {
      const fileId = doc.id || doc.file_id;
      if (!fileId || this.isReportDisabled(doc)) return;

      this.generatingReport = fileId;

      try {
        const blob = await ChatbotService.generateReport(fileId);

        if (!(blob instanceof Blob)) {
          throw new Error('Invalid report data received from server');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.name || doc.file_name || 'report'}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('Report generated and downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating report:', error);
        this.$bvToast.toast('Error generating report: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.generatingReport = null;
      }
    },

    async generateCombinedReport() {
      if (!this.documents.length || this.generatingCombinedReport) return;

      this.generatingCombinedReport = true;
      try {
        const blob = await ChatbotService.generateCombinedReport(this.candidateId);

        if (!(blob instanceof Blob)) {
          throw new Error('Invalid combined report data received from server');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.candidateName || 'Candidate'} Combined Report ${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('Combined report generated and downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating combined report:', error);
        this.$bvToast.toast('Error generating combined report: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.generatingCombinedReport = false;
      }
    },
    async deleteDocument(doc) {
      const fileId = doc.id || doc.file_id;
      if (!fileId) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${doc.name || doc.file_name || 'this document'}"?`,
        { title: 'Confirm Delete', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true }
      );
      if (!confirmed) return;
      try {
        await ChatbotService.deleteFile(fileId);
        this.$bvToast.toast('Document deleted successfully', { title: 'Success', variant: 'success', solid: true });
        await this.loadDocuments();
      } catch (err) {
        this.$bvToast.toast('Failed to delete document.', { title: 'Error', variant: 'danger', solid: true });
      }
    },
    isDocumentSelected(docId) {
      return this.selectedDocuments.some(d => d.id === docId);
    },
    toggleDocumentSelection(doc) {
      const index = this.selectedDocuments.findIndex(d => d.id === doc.id);
      if (index > -1) {
        this.selectedDocuments.splice(index, 1);
      } else {
        this.selectedDocuments.push(doc);
      }
    },
    clearSelection() {
      this.selectedDocuments = [];
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedDocuments = [...this.documents];
      } else {
        this.selectedDocuments = [];
      }
    },
    async deleteSelectedDocuments() {
      if (this.selectedDocuments.length === 0) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete ${this.selectedDocuments.length} file${this.selectedDocuments.length !== 1 ? 's' : ''}? This action cannot be undone.`,
        { title: 'Confirm Delete', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true }
      );
      if (!confirmed) return;
      this.deletingFiles = true;
      const toDelete = [...this.selectedDocuments].filter(d => d.id || d.file_id);
      try {
        await Promise.all(toDelete.map(d => ChatbotService.deleteFile(d.id || d.file_id)));
        this.$bvToast.toast(`${toDelete.length} file${toDelete.length !== 1 ? 's' : ''} deleted successfully`, { title: 'Success', variant: 'success', solid: true });
        this.selectedDocuments = [];
        await this.loadDocuments();
      } catch (err) {
        this.$bvToast.toast('Failed to delete documents.', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.deletingFiles = false;
      }
    },
    getStatusClass(status) {
      if (status === 'completed' || status === 'success') return 'status-completed';
      if (status === 'processing' || status === 'extracting') return 'status-processing';
      if (status === 'failed' || status === 'error') return 'status-error';
      return 'status-pending';
    },
    getStatusIcon(status) {
      if (status === 'completed' || status === 'success') return 'las la-check';
      if (status === 'processing' || status === 'extracting') return 'las la-sync';
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    },
    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatName(name) {
      if (!name) return '';
      const parts = name.split('.');
      return parts.length > 1 ? parts.slice(0, -1).join('.') : name;
    },
    getFileIcon(doc) {
      const documentType = doc.document_type || doc.type;
      const ext = (doc.name || doc.file_name || '').split('.').pop()?.toLowerCase();

      if (documentType) {
        switch (documentType) {
          case 'application/pdf':
          case 'pdf':
            return 'las la-file-pdf';
          case 'application/csv':
          case 'csv':
          case 'application/vnd.ms-excel':
          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          case 'excel':
          case 'xlsx':
          case 'xls':
            return 'las la-file-excel';
          case 'image/jpeg':
          case 'image/png':
            return 'las la-portrait';
        }
      }
      switch (ext) {
        case 'pdf': return 'las la-file-pdf';
        case 'csv':
        case 'xlsx':
        case 'xls':
          return 'las la-file-excel';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'las la-portrait';
        default:
          return 'las la-file';
      }
    },
    isPdfFile(doc) {
      const documentType = doc.document_type || doc.type;
      const ext = (doc.name || doc.file_name || '').split('.').pop()?.toLowerCase();
      if (documentType === 'application/pdf' || documentType === 'pdf') return true;
      if (ext === 'pdf') return true;
      return false;
    },
    isExcelFile(doc) {
      const documentType = doc.document_type || doc.type;
      const ext = (doc.name || doc.file_name || '').split('.').pop()?.toLowerCase();
      const excelTypes = ['application/csv', 'csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'excel', 'xlsx', 'xls'];
      if (documentType && excelTypes.includes(documentType)) return true;
      if (['csv', 'xlsx', 'xls'].includes(ext)) return true;
      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../chatbot/index.scss';

.bank-statement-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.bank-statement-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem 2rem;
}

.loading-state, .error-state {
  text-align: center;
  padding: 3rem 2rem;
}

.error-state i { font-size: 3rem; color: #dc2626; margin-bottom: 1rem; }
.error-state p { color: #dc2626; margin-bottom: 1rem; }

.retry-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.bank-statement-sections { display: flex; flex-direction: column; gap: 1rem; }

.bank-statement-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.bank-statement-section-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;

  .status-legend-item {
    display: inline-flex;
    align-items: center;
    font-size: 0.8125rem;
    color: #4a5568;
    font-family: $brand-font-regular;

    .status-badge {
      width: 22px;
      height: 22px;
      font-size: 0.9rem;

      i {
        font-size: 1rem;
      }
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  gap: 1rem;
}

.section-header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  gap: 1rem;
}

.section-header-buttons {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  margin-left: auto;
}

.select-all-checkbox {
  position: relative;
  top: 4px;
  left: 6.5px;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
    vertical-align: middle;
    background-color: #ffffff;
    border: 2px solid #cbd5e0;
    border-radius: 4px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;

    &:checked {
      background-color: $color-primary;
      border-color: $color-primary;

      &::after {
        content: '✓';
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
        font-weight: bold;
        line-height: 1;
      }
    }

    &:hover {
      border-color: $color-primary;
    }
  }

  span {
    font-size: 0.875rem;
    color: #495057;
    font-family: $brand-font-medium;
  }
}

.file-checkbox {
  flex-shrink: 0;
  position: relative;
  top: 4px;
  display: flex;
  align-items: center;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
    vertical-align: middle;
    background-color: #ffffff;
    border: 2px solid #cbd5e0;
    border-radius: 4px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    position: relative;

    &:checked {
      background-color: $color-primary;
      border-color: $color-primary;

      &::after {
        content: '✓';
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
        font-weight: bold;
        line-height: 1;
      }
    }

    &:hover {
      border-color: $color-primary;
    }
  }
}

.upload-section-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: auto;
  padding: 0.625rem 1rem;
  background: $color-primary;
  color: $color-secondary;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  font-family: $brand-font-medium;
}

.empty-document {
  padding: 2rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
  color: #6c757d;
  i { font-size: 3rem; display: block; margin-bottom: 0.5rem; }
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

.document-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;

  &.selected {
    border-left: 3px solid $color-secondary;
  }
}

.document-card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.document-file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;

  i {
    font-size: 1.5rem;
    color: $color-primary;
    opacity: 0.8;
  }

  &.pdf-file i {
    color: #dc2626;
  }

  &.excel-file i {
    color: #028a0f;
  }
}

.document-name { flex: 1; font-weight: 500; }
.document-date { font-size: 0.875rem; color: #6c757d; }

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 1rem;
  background: transparent;

  &.status-completed {
    color: #16a34a;
    i { color: #16a34a; }
  }

  &.status-processing {
    color: #3b82f6;
    i { color: #3b82f6; }
  }

  &.status-error {
    color: #dc2626;
    i { color: #dc2626; }
  }

  &.status-pending {
    color: #6b7280;
    i { color: #6b7280; }
  }

  i {
    font-size: 1.3rem;
  }
}

.view-btn {
  background: transparent;
  border: none;
  color: #4a5568;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    i { color: darken(#4a5568, 10%); }
  }

  i { font-size: 1.3rem; }
}

.report-btn {
  background: transparent;
  border: none;
  color: #3182ce;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    i { color: darken(#3182ce, 10%); }
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  i { font-size: 1.3rem; }
}

.report-btn-spinner {
  margin-right: 0.2rem;
  flex-shrink: 0;
}

.delete-btn {
  background: transparent;
  border: none;
  color: #dc2626;
  padding: 0.1rem;
  margin-right: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    i { color: darken(#dc2626, 10%); }
  }

  i { font-size: 1.3rem; }
}

.bulk-actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1.5rem;
  background: rgba($color-primary, 0.05);
  border: 1px solid rgba($color-secondary, 0.2);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.bulk-actions-info {
  font-size: 0.95rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.bulk-actions-buttons {
  position: relative;
  left: 11px;
  display: flex;
  gap: 0.55rem;
  align-items: center;
}

.bulk-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: transparent;
  color: #e53e3e;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: darken(#e53e3e, 15%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: 1.25rem;
    &:hover {
      color: darken(#e53e3e, 15%);
    }
  }
}

.bulk-cancel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: transparent;
  color: #4a5568;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    color: darken(#4a5568, 15%);
  }

  i {
    font-size: 1.25rem;
    &:hover {
      color: darken(#4a5568, 15%);
    }
  }
}
</style>
